import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import {Badge} from '@common-web/components/ui/badge';
import {Info} from 'lucide-react';
import {TargetFieldInfo} from '@client/pages/payroll/types';

export function DataTypeBadge(props: {dataType: TargetFieldInfo['dataType']}) {
  const meta = getDataTypeMeta(props.dataType);
  return (
    <div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Badge
                variant='gray'
                className={`inline-flex items-center gap-2 cursor-pointer text-xs ${meta.badgeClass} pl-2 pr-1.5`}
              >
                {meta.label}
                <Info className={`h-3.5 w-3.5 ${meta.iconClass}`} />
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent className='bg-internal-brand-navy text-white max-w-xs'>
            <div className='text-xs leading-relaxed'>{meta.tooltip}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function getDataTypeMeta(dt: TargetFieldInfo['dataType'] | undefined): {
  label: string;
  badgeClass: string;
  tooltip: string;
  iconClass: string;
} {
  switch (dt) {
    case 'boolean':
      return {
        label: 'Yes/No Field',
        badgeClass:
          'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200 hover:bg-fuchsia-200',
        tooltip:
          'Accepts: Y, N, Yes, No, True, False, 1, 0, T, F (case-insensitive). Values are normalized to Y/N.',
        iconClass: 'text-fuchsia-600',
      };
    case 'number':
      return {
        label: 'Number Field',
        badgeClass: 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200',
        tooltip:
          'Accepts numeric values. Commas are ignored and decimals are allowed. Examples: 1234, 1,234.56, 0.75.',
        iconClass: 'text-green-600',
      };
    case 'date':
      return {
        label: 'Date Field',
        badgeClass: 'bg-violet-100 text-violet-800 border border-violet-200 hover:bg-violet-200',
        tooltip: 'Accepts dates in formats: YYYY-MM-DD, MM/DD/YYYY, or M/D/YYYY. Time is ignored.',
        iconClass: 'text-violet-600',
      };
    case 'string':
    default:
      return {
        label: 'Text Field',
        badgeClass: 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200',
        tooltip: 'Accepts free text. Leading and trailing spaces are trimmed.',
        iconClass: 'text-blue-600',
      };
  }
}
